import { AlertCircle, Check, File, Folder, X, XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";

import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import { cn } from "@/lib/utils";

interface BatchFile {
  file: File;
  path: string[];
  status: "pending" | "uploading" | "success" | "error";
  progress: number;
  error?: string;
}

interface BatchFolder {
  name: string;
  path: string[];
}

interface BatchUploadManagerProps {
  files: BatchFile[];
  folders: BatchFolder[];
  isUploading: boolean;
  totalProgress: number;
  onCancel: () => void;
  error?: string;
  unsupportedFiles?: string[];
}

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case "success":
      return <Check className='h-4 w-4 text-green-500' />;
    case "error":
      return <AlertCircle className='h-4 w-4 text-red-500' />;
    case "uploading":
    case "pending":
      return (
        <div className='w-4 h-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin' />
      );
    default:
      return null;
  }
};

const getProgressBarClassName = (
  isUploading: boolean,
  error?: string,
  hasUnsupportedFiles?: boolean,
  validFileCount?: number,
): string => {
  if (isUploading) {
    return "bg-blue-400";
  }
  if (error) {
    return "bg-red-400";
  }
  if (hasUnsupportedFiles && validFileCount === 0) {
    return "bg-yellow-400";
  }

  return "bg-green-600";
};

const ProgressBar: React.FC<{
  isUploading: boolean;
  totalProgress: number;
  error?: string;
  hasUnsupportedFiles: boolean;
  validFileCount: number;
}> = ({
  isUploading,
  totalProgress,
  error,
  hasUnsupportedFiles,
  validFileCount,
}) => (
  <div className='w-full bg-gray-200 h-1'>
    <div
      className={cn(
        "h-full transition-all duration-300",
        getProgressBarClassName(
          isUploading,
          error,
          hasUnsupportedFiles,
          validFileCount,
        ),
      )}
      style={{ width: `${totalProgress}%` }}
    />
  </div>
);

const UnsupportedFilesSection: React.FC<{
  unsupportedFiles: string[];
  showUnsupported: boolean;
  toggleShowUnsupported: () => void;
}> = ({ unsupportedFiles, showUnsupported, toggleShowUnsupported }) => (
  <div className={cn("mb-4", unsupportedFiles.length > 0 && "border-b pb-3")}>
    <div
      className='flex items-center justify-between cursor-pointer'
      onClick={toggleShowUnsupported}
    >
      <h4 className='text-sm font-medium text-yellow-700 flex items-center'>
        <AlertCircle className='h-4 w-4 mr-1' />
        {unsupportedFiles.length} Excluded File
        {unsupportedFiles.length !== 1 && "s"}
      </h4>
      <button
        className='text-xs text-blue-600 hover:underline'
        onClick={(e) => {
          e.stopPropagation();
          toggleShowUnsupported();
        }}
      >
        {showUnsupported ? "Hide" : "Show"}
      </button>
    </div>

    {showUnsupported && (
      <div className='mt-2 p-2 bg-yellow-50 rounded border border-yellow-200'>
        <p className='text-xs text-yellow-700 mb-2'>
          The following files were excluded because they have unsupported file
          types:
        </p>
        <div className='space-y-1 max-h-28 overflow-y-auto pr-1'>
          {unsupportedFiles.map((fileName, index) => (
            <div
              key={`unsupported-${index}`}
              className='flex items-center text-sm text-yellow-800'
            >
              <XCircle className='h-4 w-4 mr-2 text-yellow-600 flex-shrink-0' />
              <span className='truncate'>{fileName}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const FolderList: React.FC<{ folders: BatchFolder[] }> = ({ folders }) => (
  <div className='mb-3'>
    <h4 className='text-sm font-medium text-gray-600 mb-1'>Folders</h4>
    <div className='space-y-1'>
      {folders.slice(0, 3).map((folder, index) => (
        <div key={index} className='flex items-center text-sm'>
          <Folder className='h-4 w-4 mr-2 text-blue-600' />
          <span className='truncate'>
            {folder.path.length > 1
              ? `${folder.path.slice(1).join("/")}/${folder.name}`
              : folder.name}
          </span>
        </div>
      ))}
      {folders.length > 3 && (
        <div className='text-xs text-gray-500'>
          +{folders.length - 3} more folders
        </div>
      )}
    </div>
  </div>
);

const FileList: React.FC<{
  files: BatchFile[];
  showCompleted: boolean;
  toggleShowCompleted: () => void;
}> = ({ files, showCompleted, toggleShowCompleted }) => {
  const pendingFiles = files.filter(
    (f) => f.status === "pending" || f.status === "uploading",
  );
  const completedFiles = files.filter((f) => f.status === "success");
  const errorFiles = files.filter((f) => f.status === "error");

  const visibleFiles = [
    ...pendingFiles,
    ...(showCompleted ? completedFiles : []),
    ...errorFiles,
  ];

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        <h4 className='text-sm font-medium text-gray-600'>
          Valid Files ({files.length})
        </h4>
        {completedFiles.length > 0 && (
          <button
            className='text-xs text-blue-600 hover:underline'
            onClick={(e) => {
              e.stopPropagation();
              toggleShowCompleted();
            }}
          >
            {showCompleted ? "Hide completed" : "Show completed"}
          </button>
        )}
      </div>

      <div className='space-y-1 mt-1'>
        {visibleFiles.length > 0 ? (
          visibleFiles.map((file, index) => (
            <div
              key={index}
              className={cn(
                "flex items-center justify-between text-sm py-1 px-2 rounded",
                ((): string => {
                  if (file.status === "error") {
                    return "bg-red-50";
                  }
                  if (file.status === "success") {
                    return "bg-green-50";
                  }

                  return "hover:bg-gray-50";
                })(),
              )}
            >
              <div className='flex items-center space-x-2 truncate'>
                <File className='h-4 w-4 text-gray-500 flex-shrink-0' />
                <span className='truncate'>
                  {file.path.length > 1
                    ? `${file.path.slice(1).join("/")}/${file.file.name}`
                    : file.file.name}
                </span>
              </div>
              <div className='flex items-center space-x-2'>
                <StatusIcon status={file.status} />
              </div>
            </div>
          ))
        ) : (
          <div className='text-sm text-gray-500 italic'>
            No files ready for upload
          </div>
        )}

        {!showCompleted && completedFiles.length > 0 && (
          <div className='text-xs text-gray-500 mt-1'>
            {completedFiles.length} completed file
            {completedFiles.length !== 1 ? "s" : ""} hidden
          </div>
        )}
      </div>
    </div>
  );
};

export const BatchUploadManager: React.FC<BatchUploadManagerProps> = ({
  files,
  folders,
  isUploading,
  totalProgress,
  onCancel,
  error,
  unsupportedFiles = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showCompleted, setShowCompleted] = useState(true);
  const [showUnsupported, setShowUnsupported] = useState(false);

  const toggleExpand = (): void => setIsExpanded((prev) => !prev);
  const toggleShowCompleted = (): void => setShowCompleted((prev) => !prev);
  const toggleShowUnsupported = (): void => setShowUnsupported((prev) => !prev);

  const validFileCount = files.length;
  const hasUnsupportedFiles = unsupportedFiles.length > 0;

  useEffect(() => {
    if (!isUploading && totalProgress === 100 && !error && validFileCount > 0) {
      const timer = setTimeout(() => {
        setIsExpanded(false);
      }, 3000);

      return (): void => {
        clearTimeout(timer);
      };
    }

    return undefined;
  }, [isUploading, totalProgress, error, validFileCount]);

  if (validFileCount === 0 && !isUploading && !hasUnsupportedFiles) {
    return null;
  }

  return (
    <div className='fixed bottom-0 right-8 z-50 w-[600px] bg-white rounded-t-xl shadow-2xl border border-gray-200'>
      <div
        className='flex items-center justify-between p-5 bg-slate-100 rounded-t-xl cursor-pointer'
        onClick={toggleExpand}
      >
        <div className='flex items-center space-x-3'>
          <Folder className='h-6 w-6 text-blue-600' />
          <span className='font-semibold text-lg'>
            {((): string => {
              if (isUploading) {
                return `Uploading ${validFileCount} item${
                  validFileCount === 1 ? "" : "s"
                } (${Math.round(totalProgress)}%)`;
              } else if (error) {
                return "Upload Failed";
              } else if (validFileCount > 0) {
                return `Uploaded ${files.filter((f) => f.status === "success").length}/${
                  validFileCount
                } item${validFileCount === 1 ? "" : "s"}`;
              } else if (hasUnsupportedFiles) {
                return `${unsupportedFiles.length} unsupported file${
                  unsupportedFiles.length === 1 ? "" : "s"
                }`;
              } else {
                return "Upload Manager";
              }
            })()}
          </span>
        </div>
        <div className='flex items-center space-x-3'>
          {hasUnsupportedFiles && (
            <div className='mr-2'>
              <span className='px-2 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full'>
                {unsupportedFiles.length} excluded
              </span>
            </div>
          )}
          {!isUploading && (
            <ButtonWithIcon
              variant='ghost'
              size='lg'
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
              icon={<X className='h-6 w-6' />}
            />
          )}
        </div>
      </div>

      <ProgressBar
        isUploading={isUploading}
        totalProgress={totalProgress}
        error={error}
        hasUnsupportedFiles={hasUnsupportedFiles}
        validFileCount={validFileCount}
      />

      {isExpanded && (
        <div className='p-5 max-h-[600px] overflow-y-auto'>
          {hasUnsupportedFiles && (
            <UnsupportedFilesSection
              unsupportedFiles={unsupportedFiles}
              showUnsupported={showUnsupported}
              toggleShowUnsupported={toggleShowUnsupported}
            />
          )}

          {folders.length > 0 && <FolderList folders={folders} />}

          {validFileCount > 0 && (
            <FileList
              files={files}
              showCompleted={showCompleted}
              toggleShowCompleted={toggleShowCompleted}
            />
          )}

          {validFileCount === 0 && hasUnsupportedFiles && (
            <div className='text-base text-gray-500 italic text-center py-4'>
              No valid files to upload
            </div>
          )}

          {error && (
            <div className='mt-5 p-3 bg-red-50 text-red-700 text-base rounded border border-red-200'>
              <div className='flex items-start'>
                <AlertCircle className='h-6 w-6 mr-3 flex-shrink-0' />
                <div>{error}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
